import { styled } from '@mui/material/styles';

export const CalendarWrapper = styled('div')({
    fontFamily: 'Rubik, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid var(--divider)',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: 'var(--light-main)',
});

export const Toolbar = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 16px',
    borderBottom: '1px solid var(--divider)',
});

export const MonthTitle = styled('div')({
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
    color: 'var(--text-primary)',
    textTransform: 'capitalize',
    minWidth: 180,
});

export const NavGroup = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
});

export const WeekdayRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    borderBottom: '1px solid var(--divider)',
});

export const WeekdayCell = styled('div')({
    padding: '8px 10px',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '14px',
    color: 'var(--text-secondary)',
    textTransform: 'capitalize',
    textAlign: 'left',
});

export const Grid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gridAutoRows: 'minmax(116px, auto)',
});

export const DayCellRoot = styled('div', {
    shouldForwardProp: prop => !['outside', 'isToday', 'isOver', 'creatable'].includes(prop),
})(({ outside, isToday, isOver, creatable }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
    minHeight: 116,
    padding: 6,
    borderRight: '1px solid var(--divider)',
    borderBottom: '1px solid var(--divider)',
    backgroundColor: isOver ? 'var(--secondary-light)' : outside ? 'var(--action-hover)' : 'var(--light-main)',
    cursor: creatable ? 'pointer' : 'default',
    transition: 'background-color 0.15s',
    '&:nth-of-type(7n)': { borderRight: 'none' },
    '&:hover': { backgroundColor: isOver ? 'var(--secondary-light)' : 'var(--action-hover)' },
    ...(isToday && { boxShadow: 'inset 0 0 0 2px var(--primary-main)' }),
}));

export const DayNumber = styled('div', {
    shouldForwardProp: prop => prop !== 'outside' && prop !== 'isToday',
})(({ outside, isToday }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
    minWidth: 22,
    height: 22,
    borderRadius: '50%',
    fontSize: 12,
    fontWeight: 500,
    color: isToday ? 'var(--light-main)' : outside ? 'var(--text-tertiary)' : 'var(--text-primary)',
    backgroundColor: isToday ? 'var(--primary-main)' : 'transparent',
    pointerEvents: 'none',
}));

export const ChipList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 0,
});

export const Chip = styled('div', {
    shouldForwardProp: prop => !['bg', 'fg', 'dragging', 'draggable', 'overlay'].includes(prop),
})(({ bg, fg, dragging, draggable, overlay }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 6px',
    borderRadius: 6,
    backgroundColor: bg,
    color: fg,
    fontSize: 12,
    lineHeight: '14px',
    fontWeight: 500,
    letterSpacing: '0.4px',
    cursor: draggable ? 'grab' : 'pointer',
    opacity: dragging ? 0.4 : 1,
    userSelect: 'none',
    overflow: 'hidden',
    '&:hover': { filter: overlay ? 'none' : 'brightness(0.97)' },
    ...(overlay && {
        cursor: 'grabbing',
        opacity: 1,
        transform: 'rotate(2deg)',
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.22)',
    }),
}));

export const ChipTime = styled('span')({
    fontWeight: 500,
    flexShrink: 0,
});

export const ChipTitle = styled('span')({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
});

export const ChipIcons = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0,
    '& svg, & img': { display: 'block' },
});

export const MoreButton = styled('button')({
    alignSelf: 'flex-start',
    border: 'none',
    background: 'transparent',
    padding: '2px 4px',
    fontFamily: 'Rubik, sans-serif',
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--primary-main)',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
});
